import type { ParsedUrlQuery } from 'querystring';
import { QueryParam } from 'types/urlParams';
import type { PageContextInterface } from 'src/context/PageContext';
import { PREVIEW_SEGMENT, PreviewLinkTarget, resolvePreviewLinkStyle } from '@/constants/previewEntities';
import { buildPublicPathFromSlug } from 'utils/publicUrlRule';

export const isPreviewLinkEnabled = (query?: ParsedUrlQuery): boolean => query?.[QueryParam.PREVIEW_LINK] === 'true';

/** The URL in preview mode on its published version: rendered without collecting anything. */
export const withPreviewLink = (url: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}${QueryParam.PREVIEW_LINK}=true`;

/**
 * URL params as the page renders them. A `previewLink` visit reads data like a public visit but
 * renders in preview mode: the banner shows, nothing is collected and links stay in preview.
 */
export const withPreviewLinkMode = <T extends { preview?: boolean; previewLink?: boolean }>(urlParams: T): T => ({
  ...urlParams,
  preview: Boolean(urlParams.preview) || Boolean(urlParams.previewLink),
});

type PreviewPathContext = Pick<PageContextInterface, 'urlParams' | 'slug' | 'linkHostname'>;

/**
 * The href for a link rendered on the current page, from its slug segments (organization first).
 * A link that stays in the admin preview keeps the default (slugged) form and the `/preview`
 * suffix; every other link drops the org segment on the org's custom host. From a preview,
 * published versions open in preview mode.
 */
export const buildPreviewAwarePath = (
  segments: string[],
  target: PreviewLinkTarget,
  { urlParams, slug, linkHostname }: PreviewPathContext,
): string => {
  const { stayInPreview, addPreviewLink } = resolvePreviewLinkStyle(target, urlParams, slug);
  const path = buildPublicPathFromSlug(
    [...segments, ...(stayInPreview ? [PREVIEW_SEGMENT] : [])],
    stayInPreview ? null : linkHostname,
  );
  return addPreviewLink ? withPreviewLink(path) : path;
};
